'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiSwaggerClientSide } from '@/shared/api';
import { Group } from '@/sharedTypes/Api';
import { Eye, Trash2, Plus } from 'lucide-react';
import toast from '@/components/ui/custom-toast';

interface IGroupsTableView {
  onEdit: (id: string) => void;
  onCreate: () => void;
}

const GroupsTableView = ({ onEdit, onCreate }: IGroupsTableView) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiSwaggerClientSide.admin.groupsList();
      if (response.data?.success && response.data.data) {
        setGroups(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      toast({
        title: 'Error loading groups',
        description: 'Failed to fetch groups. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Client-side search filter
  const filteredGroups = groups.filter((group) =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Dispatch event when filtering completes (for E2E tests)
  useEffect(() => {
    if (!loading) {
      document.dispatchEvent(new CustomEvent('groups-filter-complete', {
        detail: { count: filteredGroups.length, query: searchQuery }
      }));
    }
  }, [searchQuery, filteredGroups.length, loading]);

  const handleDeleteClick = (group: Group) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete?._id) return;

    setIsDeleting(true);
    try {
      const response = await apiSwaggerClientSide.admin.groupsDelete(groupToDelete._id);
      if (response.data?.success) {
        toast({
          title: 'Group deleted',
          description: `Group "${groupToDelete.name}" has been deleted.`,
          type: 'success',
        });
        // Refresh the list
        await fetchGroups();
      }
    } catch (error) {
      console.error('Failed to delete group:', error);
      toast({
        title: 'Error deleting group',
        description: 'Failed to delete the group. Please try again.',
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full gap-4">
        {/* Filters skeleton */}
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        {/* Table skeleton */}
        <div className="border rounded-lg overflow-hidden flex-1 min-h-0 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left"><Skeleton className="h-4 w-24" /></th>
                <th className="px-4 py-3 text-left"><Skeleton className="h-4 w-16" /></th>
                <th className="px-4 py-3 text-left"><Skeleton className="h-4 w-16" /></th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-t">
                  <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-12" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-8 w-20" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-4" data-testid="groups-table-view">
      {/* Filters */}
      <div className="flex justify-between items-center">
        <Input
          type="text"
          placeholder="Search groups..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-64"
          data-testid="groups-search-input"
        />
        <ButtonShadcn variant="default" onClick={onCreate} data-testid="groups-create-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Group
        </ButtonShadcn>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden flex-1 min-h-0 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Group Name
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Users
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                  {groups.length === 0
                    ? 'No groups created yet. Click "Create Group" to add one.'
                    : 'No groups match your search.'}
                </td>
              </tr>
            ) : (
              filteredGroups.map((group) => (
                <tr
                  key={group._id}
                  className="border-t hover:bg-gray-50 transition-colors"
                  data-testid={`groups-row-${group._id}`}
                >
                  <td className="px-4 py-3 text-sm" data-testid={`groups-row-name-${group._id}`}>{group.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-testid={`groups-row-count-${group._id}`}>
                    {group.memberCount ?? group.members?.length ?? 0}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <ButtonShadcn
                        variant="outline"
                        size="sm"
                        onClick={() => group._id && onEdit(group._id)}
                        data-testid={`groups-view-btn-${group._id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </ButtonShadcn>
                      <ButtonShadcn
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteClick(group)}
                        data-testid={`groups-delete-btn-${group._id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </ButtonShadcn>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the group "{groupToDelete?.name}"?
              This will remove the group from all samples that have it assigned.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <ButtonShadcn
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
              data-testid="groups-delete-cancel-btn"
            >
              Cancel
            </ButtonShadcn>
            <ButtonShadcn
              variant="default"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              data-testid="groups-delete-confirm-btn"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </ButtonShadcn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupsTableView;
